import axios from 'axios'
import { isString } from './utils/strings'

// https://github.com/miguelgrinberg/react-microblog/blob/main/src/MicroblogApiClient.js
export default class ApiClient {
  constructor(onError) {
    this.onError = onError

    // Create axios instance with base configuration
    this.client = axios.create({
      baseURL: '/api',
      headers: {
        'ngrok-skip-browser-warning': 'true',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        // Only add Bearer token if no Authorization header is already set
        if (!config.headers.Authorization) {
          const token = localStorage.getItem('accessToken')
          if (token && token !== 'null') {
            config.headers.Authorization = `Bearer ${token}`
          }
        }
        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor for token refresh and error handling
    this.client.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config

        // Handle 401 - try token refresh
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          originalRequest.url !== '/tokens'
        ) {
          originalRequest._retry = true

          try {
            // Attempt token refresh
            const refreshResponse = await this.putInternal('/tokens', {
              token: localStorage.getItem('accessToken'),
            })

            if (refreshResponse.ok) {
              localStorage.setItem('accessToken', refreshResponse.body.token)
              // Update the token in the original request
              originalRequest.headers.Authorization = `Bearer ${refreshResponse.body.token}`
              // Retry the original request
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Token refresh failed, return original error
            return Promise.reject(error)
          }
        }

        // Handle 500+ errors
        if (error.response?.status >= 500 && this.onError) {
          this.onError({
            ok: false,
            status: error.response.status,
            body: error.response.data,
          })
        }

        return Promise.reject(error)
      }
    )
  }

  // Convert axios response to match existing API format
  formatResponse(axiosResponse) {
    return {
      ok: axiosResponse.status >= 200 && axiosResponse.status < 300,
      status: axiosResponse.status,
      body: axiosResponse.status !== 204 ? axiosResponse.data : null,
    }
  }

  // Handle axios errors to match existing format
  formatError(error) {
    if (error.response) {
      // Server responded with error status
      return {
        ok: false,
        status: error.response.status,
        body: error.response.data,
      }
    } else if (error.request) {
      // Request was made but no response received
      return {
        ok: false,
        status: 500,
        json: async () => {
          return {
            code: 500,
            message: 'The server is unresponsive',
            description: error.message,
          }
        },
      }
    } else {
      // Error in request setup
      return {
        ok: false,
        status: 500,
        json: async () => {
          return {
            code: 500,
            message: 'Request failed',
            description: error.message,
          }
        },
      }
    }
  }

  // Internal put method for token refresh (bypasses interceptor)
  async putInternal(url, body) {
    try {
      const response = await axios.put('/api' + url, body, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'ngrok-skip-browser-warning': 'true',
        },
        withCredentials: url === '/tokens',
      })
      return this.formatResponse(response)
    } catch (error) {
      return this.formatError(error)
    }
  }

  async request(options) {
    try {
      // Build axios config
      const config = {
        method: options.method,
        url: options.url,
        params: options.query,
        headers: options.headers || {},
      }

      // Handle response type for binary data
      if (options.responseType) {
        config.responseType = options.responseType
      }

      // Handle body/data
      if (options.body !== undefined) {
        if (options.body instanceof File) {
          // File upload - use FormData
          const formData = new FormData()
          formData.append('file', options.body)
          config.data = formData
        } else if (options.body instanceof FormData) {
          config.data = options.body
          // Let axios set the Content-Type with boundary for multipart
        } else {
          // Regular JSON data
          config.data = options.body
          config.headers['Content-Type'] = 'application/json'
        }
      }

      // Special handling for tokens endpoint
      if (options.url === '/tokens') {
        config.withCredentials = true
      }

      // Add upload progress handler if provided
      if (options.onUploadProgress) {
        config.onUploadProgress = progressEvent => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            options.onUploadProgress(
              percentCompleted,
              progressEvent.loaded,
              progressEvent.total
            )
          }
        }
      }

      // Add download progress handler if provided
      if (options.onDownloadProgress) {
        config.onDownloadProgress = progressEvent => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            options.onDownloadProgress(
              percentCompleted,
              progressEvent.loaded,
              progressEvent.total
            )
          }
        }
      }

      // Pass through AbortController signal for request cancellation
      if (options.signal) {
        config.signal = options.signal
      }

      const response = await this.client(config)
      return this.formatResponse(response)
    } catch (error) {
      return this.formatError(error)
    }
  }

  async get(url, query, options) {
    return this.request({ method: 'GET', url, query, ...options })
  }

  async post(url, body, options) {
    return this.request({ method: 'POST', url, body, ...options })
  }

  async put(url, body, options) {
    return this.request({ method: 'PUT', url, body, ...options })
  }

  async patch(url, body, options) {
    return this.request({ method: 'PATCH', url, body, ...options })
  }

  async delete(url, options) {
    return this.request({ method: 'DELETE', url, ...options })
  }

  async verifyMagic(magicToken) {
    const response = await this.post('/verify_magic_token', {
      token: magicToken,
    })
    if (!response.ok) {
      return response.status === 401 ? 'fail' : 'error'
    }
    localStorage.setItem('accessToken', response.body.token)
    return 'ok'
  }

  logout() {
    localStorage.removeItem('accessToken')
  }

  isAuthenticated() {
    return localStorage.getItem('accessToken') !== null
  }
}

export const errorObjectToString = obj => {
  return Object.entries(obj)
    .map(
      ([field, errors]) =>
        `${field}: ${
          Array.isArray(errors)
            ? errors.join(', ')
            : isString(errors)
            ? errors
            : Object.values(errors).join(', ')
        }`
    )
    .join(', ')
}
